import React from "react";
import { useTranslations } from "next-intl";
import Field from "./Field";
import FilterDropdown from "./FilterDropdown";

const TradeModalBuyForm = ({
  fieldErrors,
  selectedProduct,
  buy,
  buyProducts,
  buyProductPrice,
  buyMinQuantity,
  setBuy,
  setBuyField,
  setFieldErrors,
  inp,
  parseNum,
  CLS,
}: {
  fieldErrors: Record<string, string>;
  selectedProduct: {
    id: string;
    name: string;
  } | null | undefined;
  buy: {
    product: string;
    quantity: string;
    fullName: string;
    gst: string;
    address: string;
    pincode: string;
  };
  buyProducts: {
    id: string;
    name: string;
  }[];
  buyProductPrice: number;
  buyMinQuantity: number;
  setBuy: React.Dispatch<React.SetStateAction<{
    product: string;
    quantity: string;
    fullName: string;
    gst: string;
    address: string;
    pincode: string;
  }>>
  setBuyField: (
    key: "product" | "quantity" | "fullName" | "gst" | "address" | "pincode",
  ) => (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
      Element
    >,
  ) => void;
  setFieldErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  inp: (error: string) => string;
  parseNum: (value: string) => number;
  CLS: {
    input: string;
    inputErr: string;
    label: string;
    sectionTitle: string;
    sidebarTitle: string;
    sidebarDesc: string;
    closeBtn: string;
    error: string;
    primaryBtn: string;
  };
}) => {
  const t = useTranslations("tradeModal");

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field
          label={t("buy.fields.selectProduct")}
          required
          error={fieldErrors.product}
        >
          {selectedProduct ? (
            <input
              value={
                selectedProduct.name.charAt(0).toUpperCase() +
                selectedProduct.name.slice(1)
              }
              readOnly
              className={`${CLS.input} bg-slate-50 cursor-not-allowed text-slate-600`}
            />
          ) : (
            <>
              <FilterDropdown
                label=""
                value={buy.product}
                placeholder={t("buy.placeholders.chooseProduct")}
                options={[
                  {
                    value: "",
                    label: t("buy.placeholders.chooseProduct"),
                  },
                  ...buyProducts.map((p) => ({
                    value: p.id,
                    label: p.name.charAt(0).toUpperCase() + p.name.slice(1),
                  })),
                ]}
                onChange={(value) =>
                  setBuyField("product")({
                    target: { value },
                  } as React.ChangeEvent<HTMLSelectElement>)
                }
              />
              {buy.product && buyProductPrice > 0 && (
                <div className="mt-1 text-xs font-semibold text-green-700">
                  {t("pricePerKg", {
                    price: buyProductPrice.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    }),
                  })}
                </div>
              )}
            </>
          )}
          {selectedProduct && buyProductPrice > 0 && (
            <div className="mt-1 text-xs font-semibold text-green-700">
              Price: ₹
              {buyProductPrice.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              })}{" "}
              per kg
            </div>
          )}
        </Field>
        <Field
          label={t("buy.fields.quantity")}
          required
          error={fieldErrors.quantity}
        >
          <input
            type="number"
            min={buyMinQuantity}
            step={buyMinQuantity}
            value={buy.quantity}
            onChange={(e) => {
              const raw = e.target.value;
              setBuy(
                (prev: {
                  product: string;
                  quantity: string;
                  fullName: string;
                  gst: string;
                  address: string;
                  pincode: string;
                }) => ({ ...prev, quantity: raw }),
              );
              const parsed = Number.parseInt(raw, 10);
              if (!raw || Number.isNaN(parsed)) {
                setFieldErrors((prev: Record<string, string>) => ({
                  ...prev,
                  quantity: "",
                }));
                return;
              }
              if (parsed < buyMinQuantity) {
                setFieldErrors((prev: Record<string, string>) => ({
                  ...prev,
                  quantity: t("errors.quantityInvalid"),
                }));
              } else if (parsed % buyMinQuantity !== 0) {
                setFieldErrors((prev: Record<string, string>) => ({
                  ...prev,
                  quantity: t("errors.quantityMultiple", {
                    min: buyMinQuantity,
                    ex1: buyMinQuantity * 2,
                    ex2: buyMinQuantity * 3,
                  }),
                }));
              } else {
                setFieldErrors((prev: Record<string, string>) => ({
                  ...prev,
                  quantity: "",
                }));
              }
            }}
            onBlur={() => {
              const parsed = Number.parseInt(buy.quantity, 10);
              const snapped = Number.isFinite(parsed)
                ? Math.max(
                  buyMinQuantity,
                  Math.round(parsed / buyMinQuantity) * buyMinQuantity,
                )
                : buyMinQuantity;
              setBuy(
                (prev: {
                  product: string;
                  quantity: string;
                  fullName: string;
                  gst: string;
                  address: string;
                  pincode: string;
                }) => ({ ...prev, quantity: String(snapped) }),
              );
              setFieldErrors((prev: Record<string, string>) => ({
                ...prev,
                quantity: "",
              }));
            }}
            placeholder={t("buy.placeholders.quantity")}
            className={inp(fieldErrors.quantity)}
          />
          {buyProductPrice > 0 && parseNum(buy.quantity) > 0 && (
            <p className="mt-1 text-xs font-semibold text-blue-600">
              {t("paymentMessage", {
                amount: (buyProductPrice * parseNum(buy.quantity) * 1.05).toFixed(2),
              })}
            </p>
          )}
        </Field>
      </div>
      <Field
        label={t("buy.fields.fullName")}
        required
        error={fieldErrors.fullName}
      >
        <input
          value={buy.fullName}
          onChange={setBuyField("fullName")}
          placeholder={t("buy.placeholders.fullName")}
          className={inp(fieldErrors.fullName)}
        />
      </Field>

      <Field
        label={t("buy.fields.address")}
        required
        error={fieldErrors.address}
      >
        <textarea
          value={buy.address}
          onChange={setBuyField("address")}
          placeholder={t("buy.placeholders.address")}
          rows={2}
          className={inp(fieldErrors.address)}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field
          label={t("buy.fields.pincode")}
          required
          error={fieldErrors.pincode}
        >
          <input
            value={buy.pincode}
            maxLength={10}
            onChange={(e) => {
              e.target.value = e.target.value.replace(/\D/g, "");
              setBuyField("pincode")(e);
            }}
            placeholder={t("buy.placeholders.pincode")}
            className={inp(fieldErrors.pincode)}
          />
        </Field>
        <Field label={t("buy.fields.gstOptional")}>
          <input
            value={buy.gst}
            onChange={setBuyField("gst")}
            placeholder={t("buy.placeholders.gst")}
            className={CLS.input}
          />
        </Field>
      </div>
    </div>
  );
};

export default TradeModalBuyForm;
